import { getLocale } from 'lib/store';
import { apiClient } from 'services/api-client';
import {
  LoginData,
  LoginOTPPayload,
  LoginOTPResponse,
  LoginResponse,
  ResetPasswordData,
  SignUpFlowData,
  SignUpFlowType,
  SignUpPayload,
  SignUpSuccessResponse,
  VerifyLoginOTPPayload,
  VerifyLoginOTPResponse,
} from './type';

export const signUp = async ({
  flowType,
  data,
}: {
  flowType: SignUpFlowType;
  data: SignUpFlowData;
}) => {
  return await apiClient.post('/sign-up', data, {
    params: {
      flow_type: flowType,
      locale: getLocale(),
      os: 'web',
    },
  });
};

export const signUpV2 = async ({
  flowType,
  data,
  hsbcFlow = false,
}: {
  flowType: SignUpFlowType;
  data: SignUpFlowData;
  hsbcFlow?: boolean;
}) => {
  return await apiClient.post('/sign-up-v2', data, {
    params: {
      flow_type: flowType,
      locale: getLocale(),
      os: 'web',
      hsbc_flow: hsbcFlow,
    },
  });
};

export const verifyHSBCCard = async ({
  data
}: {
  data: {
    user_id: string;
    username: string;
    hsbc_card_number: string;
  }
}) => {
  return await apiClient.post('/sign-up-v2', data, {
    params: {
      flow_type: 'verify_hsbc_card_number',
      locale: getLocale(),
      os: 'web',
    },
  });
};

export const verifyDashboardHSBCCard = async ({
  hsbc_card_number
}: {
  hsbc_card_number: string;
}) => {
  return await apiClient.post('/validate_hsbc_card', {
    hsbc_card_number
  }, {
    params: {
      locale: getLocale(),
      os: 'web',
    },
  });
};

export const login = async (data: LoginData): Promise<LoginResponse> => {
  const { auth_type, channel, ...payload } = data;
  try {
    return await apiClient.post('/login-v2', payload, {
      params: {
        locale: getLocale(),
        os: 'web',
        ...(auth_type && { auth_type }),
        ...(channel && { channel }),
      },
    });
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (
  data: ResetPasswordData
): Promise<LoginResponse> => {
  try {
    return await apiClient.post('/login-v2', data, {
      params: {
        locale: getLocale(),
        os: 'web',
        auth_type: 'temp_password_change',
      },
    });
  } catch (error) {
    throw error;
  }
};

export async function sendSignUpOTP({
  channel,
  ...data
}: LoginOTPPayload): Promise<LoginOTPResponse> {
  try {
    return await apiClient.post('/sign-up-otp', data, {
      params: {
        locale: getLocale(),
        os: 'web',
        auth_type: 'otp',
        ...(channel && { channel }),
      },
    });
  } catch (error) {
    throw error;
  }
}

export async function verifySignUpOTP({
  channel,
  ...data
}: VerifyLoginOTPPayload): Promise<VerifyLoginOTPResponse> {
  try {
    return await apiClient.post('/sign-up-otp', data, {
      params: {
        locale: getLocale(),
        os: 'web',
        auth_type: 'verify_otp',
        ...(channel && { channel }),
      },
    });
  } catch (error) {
    throw error;
  }
}

export const signOut = async () => {
  return await apiClient.get('/sign-out');
};

export const refreshToken = async () => {
  return (await apiClient.get('/refresh-token')) as LoginResponse;
};

export const signupOTP = async (
  data: SignUpPayload
): Promise<SignUpSuccessResponse> => {
  try {
    return await apiClient.post('/sign-up-otp', data, {
      params: {
        locale: getLocale(),
        os: 'web',
      },
    });
  } catch (error) {
    throw error;
  }
};

export const resendSignUpOTP = async ({
  channel,
  ...data
}: LoginOTPPayload): Promise<LoginOTPResponse> => {
  try {
    return await apiClient.post('/sign-up-otp', data, {
      params: {
        locale: getLocale(),
        os: 'web',
        auth_type: 'resend_otp',
        ...(channel && { channel }),
      },
    });
  } catch (error) {
    throw error;
  }
};
